import { useResetFinalPriceOnChange } from "@/services/backend/vbs/pricing/lib/use-reset-final-price-on-change";
import { useParams } from "react-router";
import { DocumentNotReadyAlert } from "@/shared/bsm/core/document/components/ui/document-sheet";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { WorkOrderStateDropdown } from "@/services/backend/relationships/components/work-order-state-dropdown";
import {
  useVbsWorkOrderShowQuery,
  useVbsWorkOrderUpdateWorkOrderStateMutation,
} from "@/services/backend/vbs/work-orders/api/work-order-api";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/services/backend/vbs/work-orders/components/work-order-pdf";
import { WorkOrder } from "@/services/backend/vbs/work-orders/types/work-order";
import { Separator } from "@/shared/components/ui/separator";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/vbs/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { SetWorkOrderImmutableDialog } from "@/services/backend/vbs/work-orders/components/set-work-order-immutable-dialog";
import { ConnectInvoiceExportDialogVbs } from "@/services/backend/connect/components/connect-invoice-export-vbs";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/vbs/work-orders/_components/work-order-breadcrumbs";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export function WorkOrderCompletionRoute() {
  const { workOrderId } = useParams();
  const {
    data: workOrder,
    isLoading,
    error,
  } = useVbsWorkOrderShowQuery({
    id: workOrderId!,
  });

  useWorkOrderStateGuard(
    workOrder,
    FulfillmentState.Executed,
    FulfillmentState.PartiallyExecuted,
    FulfillmentState.Cancelled,
    FulfillmentState.Completed,
  );

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="completion">
      {t("Nachbereitung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && (
        <PersistenceContextProvider>
          <ErrValidationFailedContextProvider>
            <Page workOrder={workOrder} />
          </ErrValidationFailedContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  useResetFinalPriceOnChange(workOrder.pricingId);

  // The gate of the deleted BackToPlanningDialog: only some roles may
  // hand a work order back to the planning.
  const canBackToPlanning = useFeatureAuth(
    "gsm.vbs.workorder.back-to-planning",
  );

  // A written down work order does not move anymore, an executed one is
  // cancelled or completed, and a completed or cancelled one can only be
  // planned again.
  const stateTransitions = isImmutable(workOrder)
    ? []
    : [FulfillmentState.Completed, FulfillmentState.Cancelled].includes(
          workOrder.workOrderState,
        )
      ? canBackToPlanning
        ? [FulfillmentState.Planning]
        : []
      : [FulfillmentState.Cancelled, FulfillmentState.Completed];

  // The field names of each tab: the keys of workorder.ReadyForCustomer and
  // of the validation errors of the fields shown on the tab.
  const links = [
    {
      to: "base-data",
      fieldName: [
        "referenceNumber",
        "executingBranchId",
        "beginDate",
        "endDate",
        "teamLeaderId",
        "customerReference",
        "internalComment",
        "serviceAddress",
        "serviceContact",
        "representative",
      ],
      label: t("Daten"),
    },
    {
      to: "pricing",
      fieldName: ["treatmentPrice", "incidentalCost", "extras"],
      label: t("Preise"),
    },
    {
      to: "calculation",
      label: t("Kalkulation"),
    },
    {
      to: "treatment",
      label: t("Behandlung"),
    },
    {
      to: `documentation/${workOrder.documentationId}`,
      fieldName: ["documentation"],
      label: t("Dokumentation"),
    },
    {
      to: `last-minute-risk-analysis/${workOrder.lastMinuteRiskAnalysisId}`,
      fieldName: ["lastMinuteRiskAnalysis"],
      label: t("LMRA"),
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  return (
    <DocumentPage>
      <DocumentStateAlert document={workOrder} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <DocumentPageActions>
          {!isImmutable(workOrder) && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <Separator orientation="vertical" className="min-h-8" />
              <SetWorkOrderImmutableDialog workOrder={workOrder} />
              <WorkOrderPdf workOrder={workOrder} />
              <ConnectInvoiceExportDialogVbs workOrder={workOrder} />
              <Separator orientation="vertical" className="min-h-8" />
            </>
          )}
          {isImmutable(workOrder) && <WorkOrderPdf workOrder={workOrder} />}
          <WorkOrderStateDropdown
            workOrder={workOrder}
            mutation={useVbsWorkOrderUpdateWorkOrderStateMutation}
            transitions={stateTransitions}
          />
        </DocumentPageActions>
      </DocumentPageHeader>
      {/*
        The completion page has no tab for every field the readiness check
        names - the LMRA and the documentation are edited during the execution -
        so a refused "Fertigstellen" lists them here.
      */}
      <DocumentNotReadyAlert document={t("Arbeitsschein")} />
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
